import type { Room } from '@hollowveil/shared';

export const roomFinalSanctuary: Room = {
      id: 'room_final_sanctuary',
      name: "Le Sanctuaire Final",
      description: "Le dernier défi. Deux épreuves, une énigme finale, et le Cœur du Voile t'attend.",
      size: [44, 20, 44],
      floorStyle: 'sanctuary',
      ambiance: {
        realWorld: {
          backgroundColor: '#ffd700',
          ambientLightColor: '#fff8dc',
          ambientLightIntensity: 0.6,
        },
        spectralWorld: {
          backgroundColor: '#191970',
          ambientLightColor: '#9370db',
          ambientLightIntensity: 0.45,
        },
      },
      objects: [
        // Grand entrance pillars
        {
          id: 'obj_grand_pillar_1',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [4, 0, 4],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        {
          id: 'obj_grand_pillar_2',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [20, 0, 4],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        {
          id: 'obj_grand_pillar_3',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [4, 0, 20],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        {
          id: 'obj_grand_pillar_4',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [20, 0, 20],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        // === TRIAL OF SHADOWS (Left side) ===
        {
          id: 'obj_shadow_trial_entrance',
          name: 'Entrée de l\'Épreuve des Ombres',
          type: 'static',
          modelId: 'arch',
          position: [6, 0, 8],
          rotation: [0, Math.PI / 2, 0],
          scale: [1.5, 1.5, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Shadow barrier - must pass through spectral world
        {
          id: 'obj_shadow_barrier',
          name: 'Barrière d\'Ombre',
          type: 'static',
          modelId: 'wall',
          position: [4, 0, 12],
          rotation: [0, 0, 0],
          scale: [0.3, 3, 4],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
        },
        // Shadow trial pressure plates (sequence: 1, 3, 2)
        {
          id: 'obj_shadow_plate_1',
          name: 'Dalle Ombre 1',
          type: 'trigger',
          modelId: 'block',
          position: [3, 0, 10],
          rotation: [0, 0, 0],
          scale: [1.5, 0.1, 1.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'Première dalle activée !',
              },
            },
          ],
        },
        {
          id: 'obj_shadow_plate_2',
          name: 'Dalle Ombre 2',
          type: 'trigger',
          modelId: 'block',
          position: [3, 0, 14],
          rotation: [0, 0, 0],
          scale: [1.5, 0.1, 1.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'Deuxième dalle activée !',
              },
            },
          ],
        },
        {
          id: 'obj_shadow_plate_3',
          name: 'Dalle Ombre 3',
          type: 'trigger',
          modelId: 'block',
          position: [3, 0, 16],
          rotation: [0, 0, 0],
          scale: [1.5, 0.1, 1.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: { variant: 'accent' },
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'Troisième dalle activée ! L\'Épreuve des Ombres est complète !',
              },
            },
          ],
        },
        // Shadow trial complete marker
        {
          id: 'obj_trial_shadow_complete',
          name: 'Orbe d\'Ombre',
          type: 'interactive',
          modelId: 'orb',
          position: [3, 0.5, 18],
          rotation: [0, 0, 0],
          scale: [0.8, 0.8, 0.8],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'shadow_essence',
                itemName: 'Essence des Ombres',
                itemDescription: 'L\'essence pure des ombres, preuve de ta maîtrise du monde spectral.',
                itemIcon: 'orb',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: 'Tu as réussi l\'Épreuve des Ombres ! L\'Essence des Ombres est tienne.',
              },
            },
            {
              type: 'hideObject',
              params: {
                objectId: 'obj_trial_shadow_complete',
              },
            },
          ],
        },
        // === TRIAL OF LIGHT (Right side) ===
        {
          id: 'obj_light_trial_entrance',
          name: 'Entrée de l\'Épreuve de la Lumière',
          type: 'static',
          modelId: 'arch',
          position: [18, 0, 8],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1.5, 1.5, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Light trial door requiring crystal key
        {
          id: 'obj_light_door',
          name: 'Porte de Lumière',
          type: 'interactive',
          modelId: 'animated_door',
          position: [21, 0, 12],
          rotation: [0, Math.PI / 2, 0],
          scale: [1.2, 1.5, 1],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'openDoorWithItem',
              params: {
                doorId: 'obj_light_door',
                requiredItemId: 'crystal_fragment_gold',
              },
            },
          ],
        },
        // Light riddle guardian
        {
          id: 'obj_light_guardian',
          name: 'Gardien de Lumière',
          type: 'interactive',
          modelId: 'sage',
          position: [21, 0, 16],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_light_guardian',
                  npcId: 'obj_light_guardian',
                  npcName: 'Gardien de Lumière',
                  question: '"Pour compléter l\'Épreuve de la Lumière, réponds :\n\nJe suis présent le jour, absent la nuit.\nJe donne la vie aux plantes.\nJe chasse les ombres.\nQue suis-je ?"',
                  type: 'text_input',
                  expectedAnswers: ['soleil', 'le soleil', 'sun', 'lumiere', 'lumière', 'la lumiere', 'la lumière'],
                  correctResponse: '"Le soleil ! Source de toute lumière. L\'Épreuve de la Lumière est complète."',
                  incorrectResponse: '"Non... Pense à ce qui brille dans le ciel le jour."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'addToInventory',
                      params: {
                        itemId: 'light_essence',
                        itemName: 'Essence de Lumière',
                        itemDescription: 'L\'essence pure de la lumière, preuve de ta maîtrise du monde réel.',
                        itemIcon: 'orb',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Light trial complete marker
        {
          id: 'obj_trial_light_complete',
          name: 'Orbe de Lumière',
          type: 'interactive',
          modelId: 'orb',
          position: [21, 0.5, 18],
          rotation: [0, 0, 0],
          scale: [0.8, 0.8, 0.8],
          visibleInWorld: 'real',
          interactableInWorld: 'real',
          properties: {},
          onInteract: [
            {
              type: 'showMessage',
              params: {
                message: 'L\'Orbe de Lumière brille ! Parle au Gardien pour compléter l\'épreuve.',
              },
            },
          ],
        },
        // === FINAL GUARDIAN AND HEART ===
        // Final guardian with ultimate riddle
        {
          id: 'obj_final_guardian',
          name: 'Gardien du Voile',
          type: 'interactive',
          modelId: 'spectral_remnant',
          position: [12, 0, 18],
          rotation: [0, 0, 0],
          scale: [2, 2, 2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
          onInteract: [
            {
              type: 'showRiddle',
              params: {
                riddle: {
                  id: 'riddle_final',
                  npcId: 'obj_final_guardian',
                  npcName: 'Gardien du Voile',
                  question: '"Voyageur qui a traversé les deux mondes...\n\nTu as maîtrisé les ombres et la lumière.\nMaintenant, réponds à l\'énigme finale :\n\nJe suis ce qui sépare et unit les deux mondes.\nJe suis mince comme un souffle, mais infranchissable pour les non-initiés.\nTu m\'as traversé maintes fois.\n\nQuel est mon nom ?"',
                  hint: 'Le titre de ce jeu contient la réponse...',
                  type: 'text_input',
                  expectedAnswers: ['voile', 'le voile', 'veil', 'the veil', 'hollowveil'],
                  correctResponse: '"LE VOILE... Tu as compris l\'essence même de notre monde. Tu es digne d\'atteindre mon Cœur."',
                  incorrectResponse: '"Non... Pense à ce qui sépare le monde réel du monde spectral. C\'est dans le nom même de ce lieu..."',
                  canRetry: true,
                  onCorrect: [
                    {
                      type: 'openDoor',
                      params: {
                        doorId: 'obj_heart_barrier',
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
        // Barrier to heart
        {
          id: 'obj_heart_barrier',
          name: 'Barrière du Cœur',
          type: 'static',
          modelId: 'animated_door',
          position: [12, 0, 21],
          rotation: [0, 0, 0],
          scale: [2, 2, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // The Heart of the Veil - final reward
        {
          id: 'obj_veil_heart',
          name: 'Cœur du Voile',
          type: 'interactive',
          modelId: 'crystal',
          position: [12, 1, 23],
          rotation: [0, 0, 0],
          scale: [2, 3, 2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
          onInteract: [
            {
              type: 'addToInventory',
              params: {
                itemId: 'veil_heart',
                itemName: 'Cœur du Voile',
                itemDescription: 'Le cœur même du Voile. Tu es maintenant son Gardien.',
                itemIcon: 'crystal',
              },
            },
            {
              type: 'showMessage',
              params: {
                message: '🎉 FÉLICITATIONS ! Tu as atteint le Cœur du Voile !\n\nTu as maîtrisé tous les mécanismes : le changement de monde, les énigmes, les objets, les séquences...\n\nTu es maintenant le Gardien du Voile.',
              },
            },
            {
              type: 'hideObject',
              params: {
                objectId: 'obj_veil_heart',
              },
            },
          ],
        },
        // Central platform
        {
          id: 'obj_central_platform',
          name: 'Plateforme Centrale',
          type: 'static',
          modelId: 'block',
          position: [12, 0, 12],
          rotation: [0, 0, 0],
          scale: [6, 0.3, 6],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { variant: 'stone' },
        },
        // Ancient trees
        {
          id: 'obj_sanctuary_tree_1',
          name: 'Arbre Sacré',
          type: 'static',
          modelId: 'ancient_tree',
          position: [2, 0, 2],
          rotation: [0, 0.5, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_tree_2',
          name: 'Arbre Sacré',
          type: 'static',
          modelId: 'ancient_tree',
          position: [22, 0, 2],
          rotation: [0, -0.5, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Floating orbs for atmosphere
        {
          id: 'obj_sanctuary_orb_1',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [6, 3, 12],
          rotation: [0, 0, 0],
          scale: [0.5, 0.5, 0.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_sanctuary_orb_2',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [18, 3, 12],
          rotation: [0, 0, 0],
          scale: [0.5, 0.5, 0.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_sanctuary_orb_3',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [12, 4, 6],
          rotation: [0, 0, 0],
          scale: [0.6, 0.6, 0.6],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        // === Additional decorations for enlarged sanctuary (44x44) ===
        // More sacred trees
        {
          id: 'obj_sanctuary_tree_3',
          name: 'Arbre Sacré',
          type: 'static',
          modelId: 'ancient_tree',
          position: [38, 0, 8],
          rotation: [0, 1.2, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_tree_4',
          name: 'Arbre Sacré',
          type: 'static',
          modelId: 'ancient_tree',
          position: [8, 0, 38],
          rotation: [0, 2.5, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_tree_5',
          name: 'Arbre',
          type: 'static',
          modelId: 'tree',
          position: [36, 0, 36],
          rotation: [0, 0.8, 0],
          scale: [1.3, 1.5, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // More grand pillars
        {
          id: 'obj_grand_pillar_5',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [36, 0, 4],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        {
          id: 'obj_grand_pillar_6',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [4, 0, 36],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        {
          id: 'obj_grand_pillar_7',
          name: 'Pilier Gardien',
          type: 'static',
          modelId: 'pillar',
          position: [36, 0, 36],
          rotation: [0, 0, 0],
          scale: [1.5, 2, 1.5],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: { height: 10 },
        },
        // Columns
        {
          id: 'obj_sanctuary_column_1',
          name: 'Colonne',
          type: 'static',
          modelId: 'column',
          position: [28, 0, 12],
          rotation: [0, 0, 0],
          scale: [1.2, 1.5, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_column_2',
          name: 'Colonne',
          type: 'static',
          modelId: 'column',
          position: [12, 0, 28],
          rotation: [0, 0, 0],
          scale: [1.2, 1.5, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Statues
        {
          id: 'obj_sanctuary_statue_1',
          name: 'Statue du Gardien',
          type: 'static',
          modelId: 'statue',
          position: [30, 0, 30],
          rotation: [0, -Math.PI / 4, 0],
          scale: [1.3, 1.6, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_statue_2',
          name: 'Statue du Gardien',
          type: 'static',
          modelId: 'statue',
          position: [40, 0, 20],
          rotation: [0, -Math.PI / 2, 0],
          scale: [1.2, 1.4, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Altars
        {
          id: 'obj_sanctuary_altar_1',
          name: 'Autel Sacré',
          type: 'static',
          modelId: 'altar',
          position: [32, 0, 8],
          rotation: [0, Math.PI / 4, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_altar_2',
          name: 'Autel Sacré',
          type: 'static',
          modelId: 'altar',
          position: [8, 0, 32],
          rotation: [0, -Math.PI / 4, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Torch stands
        {
          id: 'obj_sanctuary_torch_1',
          name: 'Torchère Sacrée',
          type: 'static',
          modelId: 'torch_stand',
          position: [26, 0, 6],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_torch_2',
          name: 'Torchère Sacrée',
          type: 'static',
          modelId: 'torch_stand',
          position: [6, 0, 26],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_torch_3',
          name: 'Torchère Sacrée',
          type: 'static',
          modelId: 'torch_stand',
          position: [38, 0, 26],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_torch_4',
          name: 'Torchère Sacrée',
          type: 'static',
          modelId: 'torch_stand',
          position: [26, 0, 38],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Lanterns
        {
          id: 'obj_sanctuary_lantern_1',
          name: 'Lanterne',
          type: 'static',
          modelId: 'lantern',
          position: [16, 0, 40],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_lantern_2',
          name: 'Lanterne',
          type: 'static',
          modelId: 'lantern',
          position: [40, 0, 16],
          rotation: [0, 0, 0],
          scale: [1.3, 1.3, 1.3],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Benches
        {
          id: 'obj_sanctuary_bench_1',
          name: 'Banc',
          type: 'static',
          modelId: 'bench',
          position: [24, 0, 34],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_bench_2',
          name: 'Banc',
          type: 'static',
          modelId: 'bench',
          position: [34, 0, 24],
          rotation: [0, Math.PI / 2, 0],
          scale: [1, 1, 1],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Flowers
        {
          id: 'obj_sanctuary_flower_1',
          name: 'Fleurs Sacrées',
          type: 'static',
          modelId: 'flower',
          position: [34, 0, 14],
          rotation: [0, 0.5, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_flower_2',
          name: 'Fleurs Sacrées',
          type: 'static',
          modelId: 'flower',
          position: [14, 0, 34],
          rotation: [0, 1.8, 0],
          scale: [1.2, 1.2, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // Rocks
        {
          id: 'obj_sanctuary_rock_1',
          name: 'Rocher',
          type: 'static',
          modelId: 'rock',
          position: [40, 0, 38],
          rotation: [0, 1.2, 0],
          scale: [1.2, 1, 1.2],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        {
          id: 'obj_sanctuary_rock_2',
          name: 'Rocher',
          type: 'static',
          modelId: 'rock',
          position: [2, 0, 40],
          rotation: [0, 2.5, 0],
          scale: [0.9, 0.7, 0.9],
          visibleInWorld: 'both',
          interactableInWorld: 'both',
          properties: {},
        },
        // More spectral orbs
        {
          id: 'obj_sanctuary_orb_4',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [30, 3.5, 20],
          rotation: [0, 0, 0],
          scale: [0.45, 0.45, 0.45],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_sanctuary_orb_5',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [20, 4, 30],
          rotation: [0, 0, 0],
          scale: [0.5, 0.5, 0.5],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
        {
          id: 'obj_sanctuary_orb_6',
          name: 'Orbe Sacré',
          type: 'static',
          modelId: 'orb',
          position: [38, 2.5, 38],
          rotation: [0, 0, 0],
          scale: [0.4, 0.4, 0.4],
          visibleInWorld: 'spectral',
          interactableInWorld: 'spectral',
          properties: {},
        },
      ],
      puzzles: [
        {
          id: 'puzzle_final_trials',
          name: 'Les Épreuves Finales',
          type: 'multi',
          state: 'active',
          description: 'Complète les deux épreuves et résous l\'énigme finale.',
          hint: 'L\'Épreuve des Ombres est dans le monde spectral, l\'Épreuve de la Lumière dans le monde réel.',
          linkedObjectIds: ['obj_trial_shadow_complete', 'obj_trial_light_complete', 'obj_final_guardian', 'obj_veil_heart'],
          solution: {
            type: 'items',
            expected: ['shadow_essence', 'light_essence', 'veil_heart'],
          },
          onSolve: [
            {
              type: 'showMessage',
              params: {
                message: 'Tu as complété toutes les épreuves ! Tu es le nouveau Gardien du Voile.',
              },
            },
          ],
        },
      ],
      connections: [
        {
          targetRoomId: 'room_celestial_observatory',
          position: [0, 0, 12],
          direction: 'west',
          locked: false,
        },
      ],
      initialWorld: 'real',
      hasSpectralLayer: true,
      palette: 'sanctuary',
      biome: 'tundra',
      weather: 'blizzard',
      weatherIntensity: 0.5,
      npcs: [
        {
          id: 'npc_polar_bear_1',
          type: 'polar_bear',
          name: 'Ours Polaire Ancien',
          position: [10, 0, 35],
          scale: 3.5,
          idleAnimation: 'stand',
          petAnimation: 'nuzzle',
          petRadius: 4,
          petCooldown: 5000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_arctic_fox_1',
          type: 'arctic_fox',
          name: 'Renard Arctique',
          position: [35, 0, 15],
          scale: 2.5,
          idleAnimation: 'sit',
          petAnimation: 'happy',
          petRadius: 3,
          petCooldown: 2500,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_penguin_1',
          type: 'penguin',
          name: 'Pingouin Empereur',
          position: [25, 0, 38],
          scale: 2.5,
          idleAnimation: 'stand',
          petAnimation: 'jump',
          petRadius: 2.5,
          petCooldown: 2000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_penguin_2',
          type: 'penguin',
          name: 'Petit Pingouin',
          position: [28, 0, 36],
          scale: 2,
          idleAnimation: 'stand',
          petAnimation: 'happy',
          petRadius: 2.5,
          petCooldown: 1500,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_seal_1',
          type: 'seal',
          name: 'Phoque Gardien',
          position: [40, 0, 25],
          scale: 3,
          idleAnimation: 'sleep',
          petAnimation: 'happy',
          petRadius: 3,
          petCooldown: 3000,
          isPettable: true,
          visibleInWorld: 'both',
        },
        {
          id: 'npc_rabbit_1',
          type: 'rabbit',
          name: 'Lapin des Neiges',
          position: [15, 0, 20],
          scale: 2.5,
          idleAnimation: 'sit',
          petAnimation: 'jump',
          petRadius: 2.5,
          petCooldown: 1800,
          isPettable: true,
          visibleInWorld: 'both',
        },
      ],
};
